import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Sparkles, FileText, Calculator, MessageCircle, CheckCircle2,
  ArrowRight, Star, Zap, Users, TrendingUp, Lock, ChevronRight, Quote, Clock, Award
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { inr } from "@/lib/format";

const HERO_IMG = "/images/ritika-hero.jpg";
const FEAT_IMG = "/images/ritika-about.jpg";

const HIGHLIGHTS = [
  { v: "ITR-1/2/3/4", l: "All major ITR forms supported" },
  { v: "Old & New", l: "Both regimes, compared live" },
  { v: "AI + CA", l: "Automated prep, CA-reviewed filing" },
  { v: "AES-256", l: "Bank-grade encryption, DPDP-compliant" },
];

const FEATURES = [
  { icon: Sparkles, title: "AI Tax Assistant", body: "Claude-powered chat trained on the Income Tax Act, CBDT circulars and every Union Budget. Ask anything in plain English." },
  { icon: FileText, title: "Upload, we extract", body: "Drop Form 16, broker statements or bank PDFs. Our AI reads every line and pre-fills your ITR — you just review." },
  { icon: Calculator, title: "Old vs New, instantly", body: "Side-by-side regime comparison with a recommendation. Switch regimes mid-filing and watch your tax recalculate live." },
  { icon: ShieldCheck, title: "Pre-submission audit checks", body: "Before submission our AI runs validation checks — wrong PAN, duplicate TDS, capital-gain errors — so notices don't come knocking." },
  { icon: MessageCircle, title: "Talk to a CA", body: "Premium and CA-assisted plans give you a 1:1 Chartered Accountant. Video calls, chat, document review — all on one platform." },
  { icon: Lock, title: "Bank-grade security", body: "AES-256 encryption, DPDP-ready, isolated tenants. Your PAN, Aadhaar and bank data never leave Indian data centres." },
];

const STEPS = [
  { n: "01", t: "Create your account", d: "Sign up with email or Google. Add PAN and Aadhaar — we mask them everywhere." },
  { n: "02", t: "Upload Form 16", d: "Drag and drop. The AI extracts salary, deduction and TDS lines — you review and confirm what it found." },
  { n: "03", t: "Review the prefill", d: "Walk through the wizard. Add capital gains, rental income, deductions. We compare regimes live." },
  { n: "04", t: "AI Audit + e-file", d: "Pre-submission audit checks run automatically. Submit, e-verify with Aadhaar OTP, and get your acknowledgement." },
];

const FAQ = [
  { q: "Is CA Ritika Singh a registered Chartered Accountant firm?", a: "Yes. CA Ritika Singh is led by a practising Chartered Accountant, registered with the Institute of Chartered Accountants of India (ICAI). All CA-assisted filings are reviewed and signed off by a licensed CA from our team." },
  { q: "Which ITR forms are supported?", a: "ITR-1 (salary up to ₹50L), ITR-2 (capital gains), ITR-3 (business/profession), and ITR-4 (presumptive). The platform automatically picks the right form based on your income sources." },
  { q: "How is my data protected?", a: "We use AES-256 encryption at rest, TLS 1.3 in transit, role-based access control, and our infrastructure is hosted in Indian data centres compliant with the Digital Personal Data Protection Act (DPDP), 2023." },
  { q: "Do you support both the old and new tax regimes?", a: "Yes — and that's the whole point. The platform computes your tax under both regimes side-by-side, recommends the better one, and lets you switch any time before filing." },
  { q: "What happens after I submit my return?", a: "You get an instant acknowledgement (ITR-V). E-verification is built in (Aadhaar OTP / net banking / DSC). Track your refund status from the dashboard — typical refunds land in 14-25 days." },
  { q: "Can I file for previous years?", a: "Yes. For AY 2026-27 the regular deadline is 31 July 2026. Belated and updated returns for prior years are also supported — check the Income Tax portal for the applicable deadline for your assessment year." },
];

export default function Landing() {
  const [testimonials, setTestimonials] = useState([]);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then(({ data }) => setTestimonials(data)).catch(() => {});
    api.get("/billing/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border" data-testid="hero">
        <div className="absolute inset-0 grid-pattern opacity-60 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 lg:pt-24 pb-20 lg:pb-28">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-7">
              <Badge variant="outline" className="mb-5 rounded-full px-3 py-1 border-border bg-card">
                <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-accent animate-pulse"></span>
                AY 2026-27 filing now open
              </Badge>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-balance leading-[1.05]">
                AI-Assisted Tax Practice.
                <br />
                <span className="text-primary">Reviewed &amp; approved</span>{" "}
                <span className="text-accent">by a Chartered Accountant.</span>
              </h1>
              <p className="mt-6 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed">
                AI accelerates every step of preparation. Every filing is reviewed and explicitly approved by a qualified Chartered Accountant before submission — no client document ever leaves the platform without an audit-logged CA signature.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="h-12 px-6 text-base" data-testid="hero-start-filing">
                  <Link to="/signup">
                    Start filing free
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="h-12 px-6 text-base" data-testid="hero-calculator">
                  <Link to="/calculator">
                    Try the tax calculator
                  </Link>
                </Button>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-muted-foreground">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Old & new regime supported</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> ITR-1 / 2 / 3 / 4</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Free for ITR-1</div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }} className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-3 rounded-2xl bg-gradient-to-br from-primary/8 to-accent/8 blur-2xl" />
                <Card className="relative overflow-hidden p-0 border-border">
                  <img src={HERO_IMG} alt="CA Ritika Singh" className="w-full h-[420px] object-cover object-[center_25%]" />
                  <div className="p-6 border-t border-border">
                    <div className="flex items-center gap-3">
                      <Avatar />
                      <div className="leading-tight">
                        <div className="font-display text-base font-semibold">CA Ritika Singh</div>
                        <div className="text-xs text-muted-foreground">Founder · Chartered Accountant, ICAI</div>
                      </div>
                    </div>
                    <blockquote className="mt-4 text-sm leading-relaxed text-muted-foreground">
                      "We built this platform because tax filing in India shouldn't feel like a punishment. Our AI does the heavy lifting — and a real CA reviews every premium return."
                    </blockquote>
                  </div>
                </Card>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-b border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {HIGHLIGHTS.map((s, i) => (
              <div key={i} className="text-center md:text-left">
                <div className="font-display text-3xl sm:text-4xl font-bold tracking-tight num-rupee">{s.v}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-muted-foreground">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-accent">Why CA Ritika Singh</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-balance">
            Built by Chartered Accountants. Run by AI.
          </h2>
          <p className="mt-4 text-base text-muted-foreground leading-relaxed">
            Most tax apps ask you to fill forms. We read your documents, understand your situation, and tell you exactly what to claim — with section-level citations.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="features-grid">
          {FEATURES.map((f, i) => {
            const Ic = f.icon;
            return (
              <Card key={i} className="p-6 border-border hover:border-primary/40 hover:-translate-y-1 transition-all duration-200 group">
                <div className="grid h-10 w-10 place-items-center rounded-md bg-primary/8 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Ic className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
              </Card>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-y border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-accent">How it works</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-balance">From sign-up to acknowledgement in a single sitting.</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">No spreadsheets. No CA visits. No back-and-forth WhatsApp messages. Just upload, review, file.</p>
              <img src={FEAT_IMG} alt="CA Ritika Singh — at work" className="mt-8 rounded-xl border border-border w-full object-cover h-80 object-[center_30%]" />
            </div>
            <div className="lg:col-span-7 space-y-4">
              {STEPS.map((s, i) => (
                <Card key={i} className="p-6 border-border flex gap-5 hover:border-primary/40 transition-colors">
                  <div className="font-display text-3xl font-bold text-primary/30 num-rupee shrink-0 w-12">{s.n}</div>
                  <div>
                    <h3 className="font-display text-lg font-semibold">{s.t}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="testimonials">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-accent">What people say</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">Loved by salaried, founders & freelancers.</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.slice(0, 6).map((t) => (
              <Card key={t.id} className="p-6 border-border">
                <Quote className="h-6 w-6 text-primary/30" />
                <p className="mt-4 text-sm leading-relaxed">{t.body}</p>
                <div className="mt-5 flex items-center gap-2">
                  <div className="flex">{Array(5).fill(0).map((_, i) => (<Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />))}</div>
                </div>
                <div className="mt-4 font-display text-sm font-semibold">{t.name}</div>
                <div className="text-xs text-muted-foreground">{t.role}</div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* PRICING PREVIEW */}
      {plans.length > 0 && (
        <section className="border-y border-border bg-card">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
            <div className="text-center max-w-2xl mx-auto">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-accent">Pricing</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">Pay only for what you need.</h2>
              <p className="mt-4 text-muted-foreground">From completely free self-filing to a dedicated Chartered Accountant.</p>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {plans.slice(0, 3).map((p) => (
                <Card key={p.code} className={`p-7 border-border ${p.popular ? "border-primary shadow-lg" : ""}`}>
                  {p.popular && <Badge className="bg-primary text-primary-foreground mb-3">Most popular</Badge>}
                  <div className="font-display text-xl font-semibold">{p.name}</div>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="num-rupee font-display text-4xl font-bold">{p.price === 0 ? "Free" : inr(p.price)}</span>
                    {p.price > 0 && <span className="text-sm text-muted-foreground">/ year</span>}
                  </div>
                  <ul className="mt-6 space-y-2.5">
                    {p.features.slice(0, 5).map((f, i) => (
                      <li key={i} className="flex gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 mt-0.5 text-accent shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button asChild className="mt-6 w-full" variant={p.popular ? "default" : "outline"}>
                    <Link to="/pricing">{p.cta}</Link>
                  </Button>
                </Card>
              ))}
            </div>
            <div className="mt-6 text-center">
              <Button asChild variant="ghost">
                <Link to="/pricing">See all plans <ChevronRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="faq">
        <div className="text-center">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-accent">FAQ</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">Common questions</h2>
        </div>
        <Accordion type="single" collapsible className="mt-10">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`f-${i}`} className="border-border">
              <AccordionTrigger className="text-left font-semibold" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-2xl bg-primary text-primary-foreground p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="max-w-xl">
            <h3 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">File your ITR online — AI-assisted, CA-reviewed.</h3>
            <p className="mt-3 text-primary-foreground/80 leading-relaxed">Salaried, freelancer, founder — start free, upgrade only when you need a real CA.</p>
          </div>
          <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground h-12 px-6">
            <Link to="/signup" data-testid="cta-bottom">Create my account <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Avatar() {
  return (
    <div className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary font-display font-bold">
      R
    </div>
  );
}
